import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, Save, Send, CheckCircle2, XCircle, BookCheck,
  RotateCcw, Ban, Trash2, AlertTriangle, CheckCircle,
  FileText, History, Paperclip, ShieldCheck, Search, X, Printer,
  ExternalLink, ArrowRightLeft,
} from 'lucide-react';

import { bankTransferApi } from '@/api/bankTransfer.api';
import { bankAccountApi } from '@/api/bank.api';
import { exchangeRateApi } from '@/api/exchangeRate.api';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { BankTransferVoucher } from '@/components/banking/BankTransferVoucher';
import { exportToExcel } from '@/utils/reportExport';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { ERPContextBar } from '@/components/erp/ERPContextBar';
import { WorkflowStatusPanel } from '@/components/erp/WorkflowStatusPanel';
import { AttachmentPanel } from '@/components/erp/AttachmentPanel';
import { AuditViewer } from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn } from '@/utils/cn';

import type { RootState } from '@/state/store';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn } from '@/components/master/MasterSearchModal';
import type { BankAccountListDto } from '@/types/bank.types';
import type {
  BankTransferDto,
  BankTransferWorkflowStatus,
  CreateBankTransferRequest,
  UpdateBankTransferRequest,
} from '@/types/bankTransfer.types';
import { BNK_WORKFLOW_STATUS_COLORS } from '@/types/bankTransfer.types';

// ── Search columns ─────────────────────────────────────────────────────────────

const BANK_ACCOUNT_COLUMNS: SearchColumn<BankAccountListDto>[] = [
  { key: 'accountNumber', label: 'Account No.', className: 'w-32 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'accountName',   label: 'Account Name' },
  { key: 'currencyCode',  label: 'CCY', className: 'w-14 font-mono text-slate-500 dark:text-gray-400' },
  { key: 'accountType',   label: 'Type', className: 'w-20' },
];

// ── Tabs ───────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'details',     label: 'Details',     icon: FileText    },
  { key: 'workflow',    label: 'Workflow',     icon: History     },
  { key: 'attachments', label: 'Attachments',  icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',    icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Action modal ───────────────────────────────────────────────────────────────

interface ActionModalProps {
  title:       string;
  placeholder: string;
  required:    boolean;
  onConfirm:   (text: string) => void;
  onCancel:    () => void;
}

const ActionModal = ({ title, placeholder, required, onConfirm, onCancel }: ActionModalProps) => {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">{title}</h3>
        <textarea ref={ref} value={text} onChange={e => setText(e.target.value)}
          placeholder={placeholder} rows={4}
          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none
                     focus:ring-2 focus:ring-teal-500 outline-none"
        />
        <div className="flex gap-3 mt-4 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                       hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
            Cancel
          </button>
          <button onClick={() => onConfirm(text)} disabled={required && !text.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Field helpers ──────────────────────────────────────────────────────────────

const fieldClass = (disabled: boolean) => cn(
  'w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'border-slate-200 dark:border-slate-600',
  disabled
    ? 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-gray-400 cursor-default'
    : 'focus:ring-2 focus:ring-teal-500 focus:border-transparent',
);

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Bank Account Picker ────────────────────────────────────────────────────────

interface BankPickerProps {
  label:        string;
  accountId:    string;
  accountNumber: string;
  accountName:  string;
  currencyCode: string;
  editable:     boolean;
  onOpen:       () => void;
  onClear:      () => void;
}

const BankPicker = ({ label, accountId, accountNumber, accountName, currencyCode, editable, onOpen, onClear }: BankPickerProps) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
      {label} <span className="text-red-500">*</span>
    </label>
    <div
      onClick={() => editable && onOpen()}
      className={cn(
        'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
        'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
        editable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
        !accountId && 'text-slate-400',
      )}
    >
      {accountId ? (
        <>
          <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">{accountNumber}</span>
          <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{accountName}</span>
          {currencyCode && (
            <span className="text-xs font-mono text-slate-400 shrink-0">{currencyCode}</span>
          )}
          {editable && (
            <button type="button" onClick={e => { e.stopPropagation(); onClear(); }}
              className="text-slate-300 hover:text-red-500"><X size={12} /></button>
          )}
        </>
      ) : (
        <>
          {editable && <Search size={12} className="text-slate-300" />}
          <span className="text-xs italic">{editable ? 'Select bank account…' : '—'}</span>
        </>
      )}
    </div>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────

export const BankTransferFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.BNK_TRANSFER_EDIT);
  const canSubmit  = hasPermission(Permissions.BNK_TRANSFER_SUBMIT);
  const canApprove = hasPermission(Permissions.BNK_TRANSFER_APPROVE);
  const canReject  = hasPermission(Permissions.BNK_TRANSFER_REJECT);
  const canPost    = hasPermission(Permissions.BNK_TRANSFER_POST);
  const canReverse = hasPermission(Permissions.BNK_TRANSFER_REVERSE);
  const canCancel  = hasPermission(Permissions.BNK_TRANSFER_CANCEL);
  const canDelete  = hasPermission(Permissions.BNK_TRANSFER_DELETE);
  const canPrint   = hasPermission(Permissions.BNK_TRANSFER_PRINT);
  const canAddDoc  = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDelDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const session    = useSelector((s: RootState) => s.session.session);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  const today = new Date().toISOString().split('T')[0];
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';

  // ── Header state ──────────────────────────────────────────────────────────
  const [transferDate,  setTransferDate]  = useState(today);
  const [postingDate,   setPostingDate]   = useState(today);
  const [fromAccountId,   setFromAccountId]   = useState('');
  const [fromAccountNumber, setFromAccountNumber] = useState('');
  const [fromAccountName,   setFromAccountName]   = useState('');
  const [fromAccountCode,   setFromAccountCode]   = useState('');
  const [fromCurrency,  setFromCurrency]  = useState('');
  const [toAccountId,   setToAccountId]   = useState('');
  const [toAccountNumber, setToAccountNumber] = useState('');
  const [toAccountName,   setToAccountName]   = useState('');
  const [toAccountCode,   setToAccountCode]   = useState('');
  const [amount,        setAmount]        = useState<number>(0);
  const [exchangeRate,  setExchangeRate]  = useState<number>(1);
  const [currencyCode,  setCurrencyCode]  = useState(baseCurrency);
  const [description,   setDescription]   = useState('');
  const [reference,     setReference]     = useState('');
  const [costCenterId,  setCostCenterId]  = useState('');

  const baseAmount = amount * exchangeRate;

  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen,   setToOpen]   = useState(false);

  const [transfer,  setTransfer]  = useState<BankTransferDto | null>(null);
  const [loading,   setLoading]   = useState(!isNew);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState<string | null>(null);
  const [tab,       setTab]       = useState<Tab>('details');
  const [modal,     setModal]     = useState<'approve' | 'reject' | 'reverse' | 'cancel' | 'delete' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  // Seed CC from session on new
  useEffect(() => {
    if (!isNew) return;
    setCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.costCenterId]);

  // Auto-fetch exchange rate when currency changes
  useEffect(() => {
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    exchangeRateApi.getList({ fromCurrencyCode: baseCurrency, toCurrencyCode: currencyCode, pageSize: 10 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        const sorted = items
          .filter((r: { effectiveDate: string }) => r.effectiveDate <= transferDate)
          .sort((a: { effectiveDate: string }, b: { effectiveDate: string }) => b.effectiveDate.localeCompare(a.effectiveDate));
        if (sorted.length > 0) setExchangeRate(sorted[0].rate);
        else if (items.length > 0) setExchangeRate(items[0].rate);
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, transferDate]);

  const fetchBankAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await bankAccountApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // ── Load ─────────────────────────────────────────────────────────────────
  const loadTransfer = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await bankTransferApi.getById(id);
      const t = res.data?.data;
      if (!t) throw new Error('Transfer not found');
      setTransfer(t);

      setTransferDate(t.transferDate.split('T')[0]);
      setPostingDate(t.postingDate.split('T')[0]);
      setFromAccountId(t.fromBankAccountId);
      setFromAccountCode(t.fromAccountCode);
      setFromAccountName(t.fromAccountName);
      setToAccountId(t.toBankAccountId);
      setToAccountCode(t.toAccountCode);
      setToAccountName(t.toAccountName);
      setCurrencyCode(t.currencyCode);
      setExchangeRate(t.exchangeRate);
      setAmount(t.amount);
      setDescription(t.narration ?? '');
      setReference(t.reference ?? '');
      setCostCenterId(t.costCenterId ?? '');

      // Fetch account numbers for display
      bankAccountApi.getById(t.fromBankAccountId)
        .then(r => { if (r.data?.data) setFromAccountNumber(r.data.data.accountNumber); })
        .catch(() => {});
      bankAccountApi.getById(t.toBankAccountId)
        .then(r => { if (r.data?.data) setToAccountNumber(r.data.data.accountNumber); })
        .catch(() => {});

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null,
        locationName:        null,
        costCenterCode:      null,
        costCenterName:      null,
        financialYearCode:   t.financialYearCode,
        financialPeriodCode: t.financialPeriodCode,
        transactionTypeCode: 'BNK-TRF',
        documentNumber:      t.transferNumber,
        currencyCode:        t.currencyCode,
        exchangeRate:        t.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load transfer');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadTransfer(); }, [loadTransfer]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  const status = (transfer?.workflowStatus ?? 'Draft') as BankTransferWorkflowStatus;
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable = isDraftEditable && (isNew ? hasPermission(Permissions.BNK_TRANSFER_CREATE) : canEdit);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!fromAccountId) { setError('Please select a From Bank Account.'); return; }
    if (!toAccountId)   { setError('Please select a To Bank Account.'); return; }
    if (fromAccountId === toAccountId) { setError('From and To bank accounts must be different.'); return; }
    if (amount <= 0)    { setError('Amount must be greater than zero.'); return; }

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateBankTransferRequest = {
          companyId:           authUser?.companyId ?? '',
          locationId:          null,
          costCenterId:        costCenterId || null,
          financialYearCode:   session?.financialYearCode ?? '',
          financialPeriodCode: session?.financialPeriodCode ?? '',
          transferDate,
          postingDate,
          fromBankAccountId:   fromAccountId,
          toBankAccountId:     toAccountId,
          currencyCode,
          exchangeRate,
          amount,
          reference:  reference || null,
          narration:  description || null,
        };
        const res = await bankTransferApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Transfer created successfully.');
        setTimeout(() => navigate(`/banking/transfers/${newId}`), 800);
      } else if (transfer) {
        const payload: UpdateBankTransferRequest = {
          transferDate,
          postingDate,
          fromBankAccountId: fromAccountId,
          fromAccountCode,
          fromAccountName,
          fromGLAccountId:   null,
          toBankAccountId:   toAccountId,
          toAccountCode,
          toAccountName,
          toGLAccountId:     null,
          currencyCode,
          exchangeRate,
          amount,
          reference:  reference || null,
          narration:  description || null,
        };
        await bankTransferApi.update(transfer.id, payload);
        setSuccess('Transfer updated.');
        setTimeout(() => { setSuccess(null); loadTransfer(); }, 800);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save transfer.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow actions ───────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadTransfer(); }, 1200);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Action failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit  = () => doAction(async () => { await bankTransferApi.submit(transfer!.id); }, 'Transfer submitted for approval.');
  const handlePost    = () => doAction(async () => { await bankTransferApi.post(transfer!.id); }, 'Transfer posted to general ledger.');
  const handleApprove = (remarks: string) => doAction(async () => { await bankTransferApi.approve(transfer!.id, remarks); }, 'Transfer approved.');
  const handleReject  = (reason: string)  => doAction(async () => { await bankTransferApi.reject(transfer!.id, reason); }, 'Transfer rejected.');
  const handleCancel  = (reason: string)  => doAction(async () => { await bankTransferApi.cancel(transfer!.id, reason); }, 'Transfer cancelled.');
  const handleReverse = (narr: string)    => doAction(async () => {
    const res = await bankTransferApi.reverse(transfer!.id, today, today, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/banking/transfers/${revId}`), 1200);
  }, 'Reversal transfer created.');

  const handleDelete = async () => {
    if (!transfer) return;
    setSaving(true); setError(null);
    try {
      await bankTransferApi.delete(transfer.id);
      navigate(AppRoutes.BANK_TRANSFERS, { replace: true });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (e as { message?: string })?.message ?? 'Delete failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <ERPContextBar />

      {/* Toolbar */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">

          <button onClick={() => navigate(AppRoutes.BANK_TRANSFERS)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                       hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700
                       rounded-lg transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />

          {isEditable && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              {saving ? <Spinner size="sm" /> : <Save size={14} />} Save Draft
            </button>
          )}

          {!isNew && status === 'Draft' && canSubmit && (
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Send size={14} /> Submit for Approval
            </button>
          )}

          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove && (
            <button onClick={() => setModal('approve')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <CheckCircle2 size={14} /> Approve
            </button>
          )}

          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject && (
            <button onClick={() => setModal('reject')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <XCircle size={14} /> Reject
            </button>
          )}

          {!isNew && status === 'Approved' && canPost && (
            <button onClick={handlePost} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <BookCheck size={14} /> Post to Ledger
            </button>
          )}

          {!isNew && status === 'Posted' && canReverse && !transfer?.reversalTransferId && (
            <button onClick={() => setModal('reverse')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <RotateCcw size={14} /> Reverse
            </button>
          )}

          {!isNew && !['Posted', 'Cancelled', 'Reversed'].includes(status) && canCancel && (
            <button onClick={() => setModal('cancel')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Ban size={14} /> Cancel
            </button>
          )}

          {!isNew && status === 'Draft' && canDelete && (
            <button onClick={() => setModal('delete')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Trash2 size={14} /> Delete
            </button>
          )}

          {!isNew && transfer && canPrint && (
            <button onClick={() => setPrintOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <Printer size={14} /> Print
            </button>
          )}

          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              {transfer?.isReversal && (
                <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-full">Reversal</span>
              )}
              <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', BNK_WORKFLOW_STATUS_COLORS[status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                {status}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={14} className="shrink-0" />
            <span>{success}</span>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft size={18} className="text-teal-600 dark:text-teal-400" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {isNew ? 'New Bank Transfer' : `Bank Transfer ${transfer?.transferNumber ?? ''}`}
              </h2>
            </div>
            {!isNew && transfer && (
              <p className="text-xs text-slate-400 mt-0.5">
                Created {new Date(transfer.createdDate).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isNew && transfer?.originalTransferId && (
              <button onClick={() => navigate(`/banking/transfers/${transfer.originalTransferId}`)}
                className="text-xs text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1">
                View Original <ExternalLink size={10} />
              </button>
            )}
            {!isNew && transfer?.reversalTransferId && (
              <button onClick={() => navigate(`/banking/transfers/${transfer.reversalTransferId}`)}
                className="text-xs text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1">
                View Reversal <ExternalLink size={10} />
              </button>
            )}
            {!isNew && transfer?.sourceGlJournalId && (
              <button onClick={() => navigate(`/gl/journals/${transfer.sourceGlJournalId}`)}
                className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1">
                View GL Journal <ExternalLink size={10} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
        <div className="flex">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-teal-600 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-gray-600',
              )}>
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-auto">

        {/* Details */}
        {tab === 'details' && (
          <div className="p-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* Transaction Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Transaction Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={transferDate} onChange={e => setTransferDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Posting Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Posting Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={postingDate} onChange={e => setPostingDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Currency */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Currency</label>
                  <input value={currencyCode} onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                    disabled={!isEditable} maxLength={10}
                    className={cn(fieldClass(!isEditable), 'font-mono uppercase')} />
                </div>

                {/* From Bank Account */}
                <div className="md:col-span-2 lg:col-span-1">
                  <BankPicker
                    label="From Bank Account"
                    accountId={fromAccountId}
                    accountNumber={fromAccountNumber}
                    accountName={fromAccountName}
                    currencyCode={fromCurrency}
                    editable={isEditable}
                    onOpen={() => setFromOpen(true)}
                    onClear={() => { setFromAccountId(''); setFromAccountNumber(''); setFromAccountName(''); setFromAccountCode(''); setFromCurrency(''); }}
                  />
                </div>

                {/* To Bank Account */}
                <div className="md:col-span-2 lg:col-span-1">
                  <BankPicker
                    label="To Bank Account"
                    accountId={toAccountId}
                    accountNumber={toAccountNumber}
                    accountName={toAccountName}
                    currencyCode=""
                    editable={isEditable}
                    onOpen={() => setToOpen(true)}
                    onClear={() => { setToAccountId(''); setToAccountNumber(''); setToAccountName(''); setToAccountCode(''); }}
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input type="number" value={amount || ''} onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                    disabled={!isEditable} step="0.01" min="0"
                    className={cn(fieldClass(!isEditable), 'text-right font-mono')} />
                </div>

                {/* Exchange Rate */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Exchange Rate</label>
                  <input type="number" value={exchangeRate} onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
                    disabled={!isEditable} step="0.000001" min="0.000001"
                    className={cn(fieldClass(!isEditable), 'font-mono')} />
                </div>

                {/* Base Amount (read-only) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Base Amount <span className="text-xs text-slate-400">(computed)</span>
                  </label>
                  <div className={cn(fieldClass(true), 'font-mono text-right font-semibold text-teal-700 dark:text-teal-400')}>
                    {fmt(baseAmount)}
                  </div>
                </div>

                {/* Reference */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Reference</label>
                  <input value={reference} onChange={e => setReference(e.target.value)}
                    disabled={!isEditable} placeholder="Optional" className={fieldClass(!isEditable)} />
                </div>

                {/* Cost Centre */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cost Centre</label>
                  <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    <option value="">— None —</option>
                    {costCentresForLocation(accessibleCostCenters, session?.locationId).map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                  </select>
                </div>

                {/* Financial Period (read-only from session) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Financial Period</label>
                  <div className={fieldClass(true)}>
                    {transfer?.financialPeriodCode ?? session?.financialPeriodCode ?? '—'}
                  </div>
                </div>

                {/* Description */}
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Description</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)}
                    disabled={!isEditable} rows={2} placeholder="Describe this transfer…"
                    className={cn(fieldClass(!isEditable), 'resize-none')} />
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Workflow */}
        {tab === 'workflow' && (
          <div className="p-6">
            {!isNew && transfer ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Approval Status</h3>
                <WorkflowStatusPanel entityName="BankTransfer" entityId={transfer.id} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the transfer first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {/* Attachments */}
        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && transfer ? (
              <AttachmentPanel entityName="BankTransfer" entityId={transfer.id} canAdd={canAddDoc} canDelete={canDelDoc} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the transfer first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {/* Audit */}
        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && transfer ? (
              <AuditViewer entityName="BankTransfer" entityId={transfer.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the transfer first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action modals */}
      {modal === 'approve' && (
        <ActionModal title="Approve Transfer" placeholder="Approval remarks (optional)…" required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <ActionModal title="Reject Transfer" placeholder="Rejection reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleReject(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ActionModal title="Reverse Transfer" placeholder="Reversal narration (optional)…" required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ActionModal title="Cancel Transfer" placeholder="Cancellation reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <ActionModal title="Delete Draft Transfer"
          placeholder="The draft is cancelled and kept for audit. It can no longer be edited or posted. Type a note or leave blank."
          required={false}
          onConfirm={() => { setModal(null); handleDelete(); }}
          onCancel={() => setModal(null)} />
      )}

      {/* Bank account search modals */}
      <MasterSearchModal<BankAccountListDto>
        isOpen={fromOpen}
        onClose={() => setFromOpen(false)}
        title="Select From Bank Account"
        columns={BANK_ACCOUNT_COLUMNS}
        fetchData={fetchBankAccounts}
        onSelect={item => {
          setFromAccountId(item.id);
          setFromAccountNumber(item.accountNumber);
          setFromAccountName(item.accountName);
          setFromAccountCode(item.accountNumber);
          setFromCurrency(item.currencyCode);
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setFromOpen(false);
        }}
      />

      <MasterSearchModal<BankAccountListDto>
        isOpen={toOpen}
        onClose={() => setToOpen(false)}
        title="Select To Bank Account"
        columns={BANK_ACCOUNT_COLUMNS}
        fetchData={fetchBankAccounts}
        onSelect={item => {
          setToAccountId(item.id);
          setToAccountNumber(item.accountNumber);
          setToAccountName(item.accountName);
          setToAccountCode(item.accountNumber);
          setToOpen(false);
        }}
      />

      {/* Print preview */}
      {transfer && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Bank Transfer ${transfer.transferNumber}`}
          fileBaseName={`Bank-Transfer-${transfer.transferNumber}`}
          email={{ documentType: 'Bank Transfer', documentNo: transfer.transferNumber }}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Bank Transfer',
            title: `Bank Transfer — ${transfer.transferNumber}`,
            columns: [
              { header: 'Field', key: 'field', width: 28 },
              { header: 'Value', key: 'value', width: 40 },
            ],
            rows: [
              { field: 'Document No',    value: transfer.transferNumber ?? '' },
              { field: 'Transfer Date',  value: transfer.transferDate?.slice(0, 10) ?? '' },
              { field: 'Posting Date',   value: transfer.postingDate?.slice(0, 10) ?? '' },
              { field: 'From Account',   value: `${transfer.fromAccountCode ?? ''} ${transfer.fromAccountName ?? ''}`.trim() },
              { field: 'To Account',     value: `${transfer.toAccountCode ?? ''} ${transfer.toAccountName ?? ''}`.trim() },
              { field: 'Amount',         value: transfer.amount ?? 0 },
              { field: 'Currency',       value: transfer.currencyCode ?? '' },
              { field: 'Exchange Rate',  value: transfer.exchangeRate ?? 1 },
              { field: 'Base Amount',    value: transfer.baseAmount ?? 0 },
              { field: 'Reference',      value: transfer.reference ?? '' },
              { field: 'Narration',      value: transfer.narration ?? '' },
            ],
          }, `Bank-Transfer-${transfer.transferNumber}`)}
        >
          <BankTransferVoucher
            transfer={transfer}
            companyName={authUser?.companyId ? '' : 'myLedger ONE'}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}
    </div>
  );
};
